import { Dispatch } from 'redux';
import { getNewbuildingRecommendationsApi } from '../../../../app/api/get_newbuilding_recommendations';
import { IReduxContext } from '../../store';
import { IMainPage, IMainPageAction } from '../../../types/mainpage';
import { IMakeRequest } from '../../../utils/network_helper';
import { INewbuildingRecommendations } from '../../../types/newbuilding_recommendations';
import { IThunkContext } from '../../../types/redux';
import { DEFAULT_REGIONS_DATA } from './defaultRegionsData';

export const GET_REQUEST = 'mainpage/GET_REQUEST';
export const GET_SUCCESS = 'mainpage/GET_SUCCESS';
export const GET_FAILURE = 'mainpage/GET_FAILURE';
export const GET_NEWBUILDING_RECOMMENDATIONS = 'mainpage/GET_NEWBUILDING_RECOMMENDATIONS';

export const EXPERIMENTS_GET_SUCCESS = 'mainpage/EXPERIMENTS_GET_SUCCESS';

const initialState: IMainPage = {
  agencies: [],
  contentPartData: [],
  developers: [],
  geoSeoLinks: [],
  isFetching: false,
  newSeoUrls: [],
  promoBlocks: [],
  realtors: [],
  regionsData: {
    cities: [],
    regions: DEFAULT_REGIONS_DATA,
    subdomainCityId: 0,
    subdomainRegionAllowed: true,
    subdomainRegionCoordinates: undefined,
    subdomainRegionId: 0,
    subdomainRegionInflectedTitle: '',
    subdomainRegionInflectedTitleLoct: '',
    subdomainRegionTitle: '',
  },
  section: '',
  seoData: {
    description: '',
    h1: '',
    keywords: '',
    seoText: [],
    seoTextHeading: '',
    title: '',
  },
  top3: [],
  urls: {
    addform: '',
    agencies: '',
    developers: '',
    jks: '',
    realtors: '',
  },
  newbuilding_recommendations: [],
};

const defaultAction: IMainPageAction = {
  type: '',
};

/* istanbul ignore next */
export const mainpageReducer = (state = initialState, action = defaultAction) => {
  switch (action.type) {
    case GET_REQUEST:
      return Object.assign({}, state, {
        isFetching: true,
        section: action.section,
      });

    case GET_SUCCESS:
      return Object.assign(
        {},
        state,
        {
          isFetching: false,
        },
        action.mainpage,
      );

    case GET_FAILURE:
      return Object.assign({}, state, {
        error: true,
        isFetching: false,
      });

    case EXPERIMENTS_GET_SUCCESS:
      return Object.assign({}, state, {
        experiments: action.experiments,
      });

    case GET_NEWBUILDING_RECOMMENDATIONS:
      return {
        ...state,
        newbuilding_recommendations: action.newbuilding_recommendations
          ? [...action.newbuilding_recommendations]
          : [...state.newbuilding_recommendations],
      };

    default:
      return state;
  }
};

/* istanbul ignore next */
export const mainpageRequest = (section?: string): IMainPageAction => {
  return {
    type: GET_REQUEST,
    section,
  };
};

/* istanbul ignore next */
export const experimentsSuccess = (data: { experiments: {} }): IMainPageAction => {
  return {
    experiments: data.experiments,
    type: EXPERIMENTS_GET_SUCCESS,
  };
};

/* istanbul ignore next */
export const mainpageSuccess = (response: { data: IMainPage }): IMainPageAction => {
  return {
    mainpage: response.data,
    type: GET_SUCCESS,
  };
};

/* istanbul ignore next */
export const mainpageFailure = (): IMainPageAction => {
  return {
    type: GET_FAILURE,
  };
};

/* istanbul ignore next */
export const getNewbuildingRecommendations = (recommendations: INewbuildingRecommendations[]): IMainPageAction => {
  return {
    type: GET_NEWBUILDING_RECOMMENDATIONS,
    newbuilding_recommendations: recommendations,
  };
};

export const getMainpageData = (
  section: string,
  host: string,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  dispatch: Dispatch<any>,
  context: IThunkContext,
) => {
  const getSectionDataV2Timeout = context.config.get<number>('frontendMainpage.sectionDataV2Timeout') || 3000;

  return context
    .makeRequest({
      method: 'GET',
      timeout: getSectionDataV2Timeout,
      uri: {
        host,
        path: `/cian-api/site/v1/get-${section}-data-v2/`,
      },
    })
    .then((res: { data: IMainPage }) => {
      dispatch(mainpageSuccess(res));
    })
    .catch(() => {
      dispatch(mainpageFailure());
    });
};

export const getExperimentsFlags = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>,
  makeRequest: IMakeRequest,
  activeExperiments: string[],
  queryString?: string,
) => {
  const apiBaseUrl = '/cian-api/site/v1/get-ab-experiments/';

  const experimentsParams = activeExperiments.reduce(
    (acc, experimentName, index, arr) => acc + `experiments=${experimentName}${index < arr.length - 1 ? '&' : ''}`,
    '',
  );

  return makeRequest({
    method: 'GET',
    uri: {
      path: `${apiBaseUrl}?${experimentsParams}${experimentsParams && queryString ? '&' + queryString : ''}`,
    },
    withCredentials: true,
  })
    .then((res: { data: { experiments: {} } }) => {
      dispatch(experimentsSuccess(res.data));
    })
    .catch((err: Error) => {
      throw err;
    });
};

export const ACTIVE_EXPERIMENTS = [
  'mainpage_newbuilding_search',
  'audience_mainpage_filters_price',
  'audience_mainpage_filters',
  'newbuilding_recommendations',
  'main_page_property_guides',
];

/* istanbul ignore next */
export const getMainpage = (section: string, host = '') => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (dispatch: Dispatch<any>, _: () => void, context: IThunkContext) => {
    dispatch(mainpageRequest(section));

    return getMainpageData(section, host, dispatch, context);
  };
};

/* istanbul ignore next */
export const getNewbuildingRecommendationsData = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (dispatch: Dispatch<any>, getState: () => any, context: IReduxContext) => {
    const {
      mainpage: {
        regionsData: { subdomainRegionId, subdomainCityId },
      },
    } = getState();
    const regionDraw = subdomainRegionId || subdomainCityId;

    return getNewbuildingRecommendationsApi(context.makeRequest, regionDraw).then(data => {
      dispatch(getNewbuildingRecommendations(data));
    });
  };
};
